import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSliceBalance } from '../hooks/useSliceBalance';
import { useSlicePot } from '../hooks/useSlicePot';
import { supabase } from '../lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowUpRight, ArrowDownLeft, History, Heart, RefreshCw, Clock } from 'lucide-react';

const TX_LABELS = {
  subscription_grant: 'Subscription Grant',
  purchase: 'Slice Purchase',
  crowdfunding_back: 'Campaign Backed',
  content_watch: 'Content Donation',
  refund: 'Refund',
  admin_grant: 'Admin Grant',
  admin_deduct: 'Admin Deduction',
  crowdfunding_refund: 'Campaign Refund',
  pot_vote: 'Community Fund vote',
  leftover_forfeit: 'Unused slices joined the Community Fund',
};

/** Returns icon component + Tailwind colour classes for a transaction row. */
const getTxStyle = (tx) => {
  if (tx.transaction_type === 'pot_vote') {
    return { Icon: Heart, bg: 'bg-amber-500/15', text: 'text-amber-500' };
  }
  if (tx.transaction_type === 'leftover_forfeit') {
    return { Icon: RefreshCw, bg: 'bg-amber-500/15', text: 'text-amber-500' };
  }
  const credit = tx.amount > 0;
  return {
    Icon: credit ? ArrowUpRight : ArrowDownLeft,
    bg: credit ? 'bg-primary/15' : 'bg-destructive/15',
    text: credit ? 'text-primary' : 'text-destructive',
  };
};

function formatCountdown(endsAt) {
  const diff = new Date(endsAt) - Date.now();
  if (diff <= 0) return 'Voting has ended';
  const d = Math.floor(diff / 86_400_000);
  const h = Math.floor((diff % 86_400_000) / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (d > 0) return `${d}d ${h}h remaining`;
  if (h > 0) return `${h}h ${m}m remaining`;
  return `${m}m remaining`;
}

const TIER_AMOUNTS = { Basic: 8, Standard: 15, Premium: 25 };

/**
 * V2 — Slice transaction history page at /slices.
 * Shows balance summary card and full transaction list.
 */
const SliceHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { balance, tier, periodEnd, isLoading: balanceLoading } = useSliceBalance();
  const { summary, isLoading: potLoading } = useSlicePot();
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [potCountdown, setPotCountdown] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchTransactions();
  }, [user]);

  useEffect(() => {
    const endsAt = summary?.period?.ends_at;
    if (!endsAt) return;
    const tick = () => setPotCountdown(formatCountdown(endsAt));
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [summary?.period?.ends_at]);

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const { data } = await supabase
        .from('slice_transactions')
        .select(`
          *,
          work:work_id (id, title, slug),
          campaign:crowdfunding_campaign_id (id, title)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);
      setTransactions(data || []);
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const isCredit = (tx) => tx.amount > 0;

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const nextReset = periodEnd
    ? new Date(periodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    : null;

  const tierAmount = TIER_AMOUNTS[tier];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <History className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Slice History</h1>
        </div>

        {/* Balance Summary Card */}
        <Card className="mb-8 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-6">
            {balanceLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-4 w-48" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <p className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold tabular-nums">{balance ?? 0}</span>
                  <span className="text-muted-foreground">slices available</span>
                </p>
                {tier && tierAmount ? (
                  <p className="text-sm text-muted-foreground">
                    {tier} plan · {tierAmount} slices/month
                    {nextReset && ` · Resets ${nextReset}`}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No active subscription
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Community Fund Card */}
        {!potLoading && summary && (
          <Card className="mb-8 border-amber-500/20 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-background overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                {/* Left: pot info */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl select-none" aria-hidden>🍕</span>
                    <p className="text-xs uppercase tracking-widest text-amber-500">
                      Community Fund
                    </p>
                  </div>

                  <div>
                    <p className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold tabular-nums">
                        {summary.pot_balance ?? 0}
                      </span>
                      <span className="text-sm text-muted-foreground">slices in the pot</span>
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Unused slices roll in each month — the community votes on where it goes.
                    </p>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm">
                      Your stake in this week's pick:{' '}
                      <span className="font-semibold">
                        {summary.my_votes_this_period ?? 0} slices
                      </span>
                    </p>
                    {potCountdown && (
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {potCountdown}
                      </p>
                    )}
                  </div>
                </div>

                {/* Right: CTA */}
                <div className="shrink-0 self-start sm:self-center">
                  <Button
                    asChild
                    size="sm"
                    className="rounded-full bg-amber-500 text-white hover:bg-amber-600 text-[10px] uppercase tracking-[0.18em]"
                  >
                    <Link to="/crowdfund">Vote with Your Slices</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transaction List */}
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-4">
            Transactions
          </p>

          {isLoading ? (
            <div className="space-y-1">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-lg">
                  <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-44" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="h-4 w-10 shrink-0" />
                </div>
              ))}
            </div>
          ) : transactions.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center space-y-2">
                <p className="text-4xl">🍕</p>
                <p className="font-medium">No transactions yet</p>
                <p className="text-sm text-muted-foreground">
                  Start watching and donating slices to see your history here.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-0.5">
              {transactions.map((tx) => {
                const credit = isCredit(tx);
                const label = TX_LABELS[tx.transaction_type] || tx.transaction_type;
                const { Icon, bg, text } = getTxStyle(tx);

                let contextLink = null;
                if (tx.work?.title) {
                  contextLink = (
                    <Link
                      to={`/watch/${tx.work.slug || tx.work.id}`}
                      className="hover:text-primary transition-colors"
                    >
                      {tx.work.title}
                    </Link>
                  );
                } else if (tx.campaign?.title) {
                  contextLink = (
                    <Link
                      to={`/crowdfund/${tx.campaign.id}`}
                      className="hover:text-primary transition-colors"
                    >
                      {tx.campaign.title}
                    </Link>
                  );
                }

                return (
                  <div
                    key={tx.id}
                    className="flex items-center gap-4 p-4 rounded-lg hover:bg-accent/40 transition-colors"
                  >
                    {/* Icon */}
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${bg} ${text}`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-snug">{label}</p>
                      {(contextLink || tx.description) && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {contextLink || tx.description}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDate(tx.created_at)}
                      </p>
                    </div>

                    {/* Amount */}
                    <div
                      className={`text-sm font-semibold tabular-nums shrink-0 ${
                        credit ? 'text-primary' : 'text-destructive'
                      }`}
                    >
                      {credit ? '+' : ''}{tx.amount}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SliceHistory;
